<template>
  <div class="content-info">
    <div class="content-info__header">
      <span class="content-info__header__content-type">
        {{ content.content_type }}
      </span>
      <span
        v-if="content.content_type === 'lab' && labNotAvailable"
        class="content-info__header__lab-unavailable"
      >
        This lab is not available during the trial period.
        <a href="mailto:support@ine.com"> Contact INE </a>
        to start your paid subscription now.
      </span>
    </div>
    <div class="content-info__name">{{ content.name }}</div>
    <div class="content-info__data">
      <span
        class="content-info__data-learning-area"
        :style="`--la-color: ${learningAreaColor}`"
      >
        {{ learningAreaName }}
      </span>
      <span v-if="hasSkillCheck" class="content-info__data-skill-check">
        <SkillCheckIcon class="content-info__data-skill-check-icon" />
        Skill Check
      </span>
      <span class="content-info__data-duration">{{ duration }}</span>
      <span
        v-if="content.last_activity_date"
        class="content-info__data-time-ago"
      >
        Active {{ activeTimeAgo }}
      </span>
      <span v-if="instructorName" class="content-info__data-instructor-name">
        {{ instructorName }}
        {{ instructorCounter }}
      </span>
    </div>
  </div>
</template>

<script>
import { labCommons } from '@/helpers/mixins'
import { formatDistanceToNow } from 'date-fns'
import SkillCheckIcon from '@/assets/icons/skill_check_icon.svg'

export default {
  name: 'ContentInfo',
  components: {
    SkillCheckIcon,
  },
  mixins: [labCommons],
  props: {
    content: {
      type: Object,
      required: true,
    },
    learningAreaName: {
      type: String,
      required: true,
    },
    learningAreaColor: {
      type: String,
      required: true,
    },
  },
  computed: {
    instructorName() {
      const { instructors } = this.content
      return instructors && instructors.length ? instructors[0].name : null
    },
    instructorCounter() {
      const { instructors } = this.content
      return instructors && instructors.length > 1
        ? `+${instructors.length - 1}`
        : null
    },
    duration() {
      return this.$duration.durationFromSecs(this.content.duration_in_seconds)
    },
    activeTimeAgo() {
      return formatDistanceToNow(new Date(this.content.last_activity_date), {
        addSuffix: true,
      })
    },
    hasSkillCheck() {
      return this.content.has_skill_check_labs || false
    },
  },
}
</script>

<style lang="scss" scoped>
.content-info {
  display: flex;
  flex-direction: column;
  flex: 1;
  padding: 10px;
  color: var(--white);
  font-family: var(--font-book);
  font-size: rem(12px);
  line-height: rem(14px);

  @include mobile {
    padding: 20px;
  }

  &__header {
    font-size: rem(10px);
    line-height: rem(12px);

    @include mobile {
      font-size: rem(12px);
      line-height: rem(14px);
    }

    &__content-type {
      text-transform: uppercase;
    }
    &__lab-unavailable {
      color: var(--performance-light);
      font-style: italic;
      padding-left: 10px;
      margin-left: 10px;
      border-left: 1px solid var(--white);
      a {
        color: inherit;
        text-decoration: underline;
      }
    }
  }
  &__name {
    margin: 10px 0 5px;
    font-family: var(--font-bold);
    font-size: rem(12px);
    line-height: rem(14px);
    height: 42px;
    overflow: hidden;
    text-overflow: ellipsis;

    @include mobile {
      font-family: var(--font-medium);
      font-size: rem(16px);
      line-height: rem(23px);
      height: 23px;
    }
  }
  &__data {
    height: 28px;

    @include mobile {
      height: 14px;
    }
    &-learning-area {
      &::before {
        content: '';
        display: inline-block;
        width: 10px;
        height: 10px;
        border-radius: 50%;
        background-color: var(--la-color);
        margin-right: 5px;
      }
    }
    &-skill-check {
      display: inline-flex;
      align-items: center;
      vertical-align: middle;

      &-icon {
        width: 15px;
        height: 15px;
        margin-right: 5px;
        vertical-align: middle;
      }
    }
    span {
      border-right: 1px solid var(--perfect-gray);
      padding: 0 10px;

      &:first-child {
        padding-left: 0;
      }
      &:last-child {
        border-right: none;
      }
    }
  }
}
</style>
